// src/components/context/StationContext.js
import React, { createContext, useEffect, useState, useCallback, useContext } from 'react';
import api from '../../config/axiosInstance';
import { UserContext } from './UserContext';

export const StationContext = createContext();

export const StationProvider = ({ children }) => {
  const { sessionReady, isAuthenticated } = useContext(UserContext);
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState({ id: null, name: 'Seleccione una estación' });
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  const reloadStations = useCallback(() => setRefreshTick(t => t + 1), []);
  useEffect(() => {
    if (!sessionReady) {
      return;
    }

    if (!isAuthenticated) {
      setStations([]);
      setSelectedStation({ id: null, name: 'Seleccione una estación' });
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [stRes, prefRes] = await Promise.all([
          api.get('station'),
          api.get('/user/preferences'),
        ]);
  
        const list = Array.isArray(stRes.data) ? stRes.data : [];
        const enabled = list.filter(s => s.enabled !== false);
        setStations(list);


        const prefId = prefRes?.data?.last_station_id ?? null;
        const hinted = localStorage.getItem('last_station_id');
        let chosen =
        (prefId && enabled.find(s => String(s.id) === String(prefId))) ||
        (hinted && enabled.find(s => String(s.id) === String(hinted))) ||
        enabled[0] || null;
  
        const toState = chosen
          ? { id: chosen.id, name: chosen.name }
          : { id: null, name: enabled.length === 0 ? 'Sin estaciones' : 'Seleccione una estación' };
  
        if (!cancelled) {
          setSelectedStation(toState);
          setLoading(false);               // ⬅️ liberar UI ya mismo
        }
  
        // Si hicimos fallback, sincronizá sin bloquear la UI
        if (chosen && String(chosen.id) !== String(prefId)) {
          try {
            await api.request({
              method: 'PUT',
              url: '/user/preferences',
              params: { last_station_id: Number(chosen.id) }, // ← query param
            });
            localStorage.setItem('last_station_id', String(chosen.id));
          } catch { /* silenciar */ }
        }
      } catch (err) {
        if (err?.response?.status !== 401) {
          console.error('Error cargando estaciones/preferencia', err);
        }
        if (!cancelled) {
          setStations([]);
          setSelectedStation({ id: null, name: 'Seleccione una estación' });
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, refreshTick, sessionReady]);

  const selectStation = useCallback(async (station) => {
    if (String(station?.id) === String(selectedStation?.id)) return;
    const prev = selectedStation;
    setSelectedStation({ id: station.id, name: station.name });
  
    try {
      await api.request({
        method: 'PUT',
        url: '/user/preferences',
        params: { last_station_id: Number(station.id) }, // ← query param
      });
      localStorage.setItem('last_station_id', String(station.id));
    } catch (err) {
      if (err?.response?.status === 403) setSelectedStation(prev);
      console.error('No se pudo guardar la preferencia', err);
    }
  }, [selectedStation]);

  return (
    <StationContext.Provider value={{
      stations,
      selectedStation,
      loading,
      selectStation,
      reloadStations,
    }}>
      {children}
    </StationContext.Provider>
  );
};
